/**
 * Provisioning Tokens — admin page for managing endpoint
 * registration tokens. Backed by:
 *   GET    /v1/provisioning-tokens          → list
 *   POST   /v1/provisioning-tokens          → create
 *   DELETE /v1/provisioning-tokens/:jwt_id  → revoke
 *
 * Layout mirrors `EndpointInventoryPage`: AdminViewHeader with an
 * action chip ("Create token"), stats bar with count, table styled
 * like the trace listing (sticky header, thin row borders).
 *
 * Two ephemeral modals launch via OverlayDialog:
 *   - Create form (Duration + Max uses) when the action chip is open.
 *   - One-time JWT display when a token has just been created — the
 *     gateway only returns the JWT in the create response, so we
 *     show it prominently with a copy button until the user dismisses.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { restGet, restPost, restDelete } from "../lib/restClient.js";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import { useEndpointViewTabs } from "../components/EndpointViewTabs.jsx";
import OverflowTooltip from "../components/OverflowTooltip.jsx";
import DataTable from "../components/DataTable.jsx";
import ColumnPicker from "../components/ColumnPicker.jsx";
import OverlayDialog from "../components/OverlayDialog.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialCopy, denialOrRawMessage } from "../lib/denialReason.js";
import { ACTION_TEXT_CLASS, NOTE_CLASS } from "../lib/ui.js";
import EmptyState from "../components/EmptyState.jsx";
import { formatCompact } from "../lib/renderers.js";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

// ── Schema ───────────────────────────────────────────────────────

const COLUMNS = [
    { id: "status",         label: "Status",       width: 100 },
    { id: "createdAt",      label: "Created",      width: 130 },
    { id: "expiresAt",      label: "Expires",      width: 130 },
    { id: "usage",          label: "Used",         width: 90 },
    { id: "createdByEmail", label: "Created by",   width: 220 },
    { id: "jwtId",          label: "Token ID",     width: 200 },
    { id: "_actions",       label: "",             width: 60 },
];

export function getProvisioningTokenColumns(canManageTokens) {
    return canManageTokens ? COLUMNS : COLUMNS.filter((column) => column.id !== "_actions");
}

export function provisioningTokenEmptyState(canManageTokens, error = null) {
    if (error) return { title: "Token list unavailable", note: error };
    return {
        title: "No provisioning tokens",
        note: canManageTokens
            ? "Create one above to let endpoints self-register against this tenant."
            : "No provisioning tokens have been created for this tenant.",
    };
}

const PAGE_SIZE = 1000;

// Duration is entered in days but the API takes `duration_hours`, so
// the form converts days → hours (×24) at submit. Defaults match the
// legacy dashboard (7 days / 10 uses, no upper cap) for parity (O-1162).
const DEFAULT_DURATION_DAYS = 7;
const DEFAULT_MAX_USES = 10;

// ── Row shape ────────────────────────────────────────────────────

function flattenToken(t) {
    const created = t.createdAt ?? t.created_at ?? null;
    const expires = t.expiresAt ?? t.expires_at ?? null;
    const revokedAt = t.revokedAt ?? t.revoked_at ?? null;
    const createdDate = created ? new Date(created) : null;
    const expiresDate = expires ? new Date(expires) : null;
    const revoked = Boolean(t.revoked);
    const expired = expiresDate ? expiresDate.getTime() < Date.now() : false;
    const status = revoked ? "revoked" : expired ? "expired" : "active";
    return {
        jwtId:               t.jwtId  ?? t.jwt_id  ?? "",
        createdAt:           Number.isFinite(createdDate?.getTime?.()) ? createdDate : null,
        expiresAt:           Number.isFinite(expiresDate?.getTime?.()) ? expiresDate : null,
        revokedAt:           revokedAt ? new Date(revokedAt) : null,
        maxUses:             Number(t.maxUses ?? t.max_uses ?? 0),
        endpointsRegistered: Number(t.endpointsRegistered ?? t.endpoints_registered ?? 0),
        createdByEmail:      t.createdByEmail ?? t.created_by_email ?? "",
        revoked,
        status, // "active" | "expired" | "revoked"
    };
}

// ── Cell content ─────────────────────────────────────────────────

const STATUS_DOT = {
    active:  "bg-[color:var(--green)]",
    expired: "bg-[color:var(--muted-soft)]",
    revoked: "bg-red-400",
};

function StatusCell({ status }) {
    return (
        <span className="inline-flex items-center gap-[6px] text-[11px] leading-[1.08] font-medium">
            <span className={`h-[8px] w-[8px] flex-none rounded-[999px] ${STATUS_DOT[status] ?? "bg-[color:var(--muted-soft)]"}`} />
            <span className="capitalize">{status}</span>
        </span>
    );
}

function TimestampCell({ value }) {
    if (!(value instanceof Date)) {
        return <span className="text-[color:var(--muted-soft)]">—</span>;
    }
    return (
        <div className="grid gap-0">
            <strong className="text-[10px] leading-[1.12] font-medium">
                {timeLabelFormatter.format(value)}
            </strong>
            <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">
                {dayLabelFormatter.format(value)}
            </span>
        </div>
    );
}

function UsageCell({ used, max }) {
    const exhausted = max > 0 && used >= max;
    return (
        <span className={[
            "text-[11px] leading-[1.08] font-medium",
            exhausted ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]",
        ].join(" ")}>
            <span>{used}</span>
            <span className="text-[color:var(--muted-soft)]"> / {max || "∞"}</span>
        </span>
    );
}

// ── Inline overlays — create form, JWT result ────────────────────

function CreateForm({ onCreate, busy, error }) {
    const [days, setDays] = useState(DEFAULT_DURATION_DAYS);
    const [maxUses, setMaxUses] = useState(DEFAULT_MAX_USES);

    function submit(e) {
        e.preventDefault();
        const d = Math.max(1, Number(days) || DEFAULT_DURATION_DAYS);
        const m = Math.max(1, Number(maxUses) || 1);
        onCreate({ durationHours: d * 24, maxUses: m });
    }

    return (
        <form onSubmit={submit} className="grid gap-[12px]">
            <div className="grid gap-[10px] [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)] max-[600px]:grid-cols-1">
                <label className="grid gap-[4px]">
                    <span className={NOTE_CLASS}>Duration (days)</span>
                    <input
                        type="number"
                        min="1"
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                        className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none"
                    />
                </label>
                <label className="grid gap-[4px]">
                    <span className={NOTE_CLASS}>Max uses</span>
                    <input
                        type="number"
                        min="1"
                        value={maxUses}
                        onChange={(e) => setMaxUses(e.target.value)}
                        className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none"
                    />
                </label>
            </div>
            <span className={`${NOTE_CLASS} text-[10px]`}>
                The JWT is shown once after creation. Save it before closing the result.
            </span>
            {error ? (
                <span className="text-[11px] text-red-400">{error}</span>
            ) : null}
            <div className="flex items-center justify-end">
                <button
                    type="submit"
                    disabled={busy}
                    className="h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer"
                >
                    {busy ? "Creating…" : "Create token"}
                </button>
            </div>
        </form>
    );
}

function JwtResult({ jwt, jwtId, expiresAt }) {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(jwt);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    }, [jwt]);
    const expDate = expiresAt ? new Date(expiresAt) : null;
    return (
        <div className="grid gap-[10px]">
            <span className={`${NOTE_CLASS} text-[11px] leading-[1.5]`}>
                Copy this JWT now. It will not be shown again or stored on the server.
            </span>
            <div className="grid gap-[6px] [grid-template-columns:minmax(0,1fr)_auto] items-stretch">
                <pre className="m-0 overflow-x-auto whitespace-nowrap rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[6px] text-[11px] leading-[1.4] font-mono text-[color:var(--ink)]">
                    {jwt}
                </pre>
                <button
                    type="button"
                    onClick={copy}
                    className="rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                >
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
            {jwtId ? (
                <div className={`${NOTE_CLASS} text-[10px]`}>
                    Token ID:{" "}
                    <span className="font-mono text-[color:var(--ink)]">{jwtId}</span>
                    {expDate ? (
                        <>
                            {" · expires "}
                            <span className="text-[color:var(--ink)]">
                                {dayLabelFormatter.format(expDate)} {timeLabelFormatter.format(expDate)}
                            </span>
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────

export default function ProvisioningTokensPage({ viewDef }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Inline overlay state. One of: null | "form" | { jwt, jwtId, expiresAt }.
    const [overlay, setOverlay] = useState(null);
    const [createBusy, setCreateBusy] = useState(false);
    const [createError, setCreateError] = useState(null);

    // Per-row revoke state. Map<jwtId, "pending" | error>.
    const [revoking, setRevoking] = useState({});
    // jwtId currently in confirm state.
    const [confirmRevoke, setConfirmRevoke] = useState(null);

    // Create + revoke are management actions. Hide (don't disable) them
    // for a reader who can list tokens but can't manage them — the page
    // stays useful as a read-only inventory. Fails open pre-RBAC.
    const caps = useMyPermissions();
    const canManageTokens = hasPermission(caps, PERMISSIONS.PROVISIONING_TOKENS_MANAGE);
    const canInstallers = hasPermission(caps, PERMISSIONS.INSTALLERS_READ);
    const endpointTabs = useEndpointViewTabs({ active: "tokens", canReadTokens: true, canInstallers });

    const refetch = useCallback(async (toPage = page) => {
        setLoading(true);
        setError(null);
        try {
            const resp = await restGet("/v1/provisioning-tokens", { page: toPage, page_size: PAGE_SIZE });
            const list = (resp?.tokens ?? []).map(flattenToken);
            setRows((prev) => (toPage === 0 ? list : [...prev, ...list]));
            setTotal(resp?.total ?? list.length);
        } catch (err) {
            setError(denialCopy(err, caps).message);
        } finally {
            setLoading(false);
        }
    }, [page, caps]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await restGet("/v1/provisioning-tokens", { page, page_size: PAGE_SIZE });
                if (cancelled) return;
                const list = (resp?.tokens ?? []).map(flattenToken);
                setRows((prev) => (page === 0 ? list : [...prev, ...list]));
                setTotal(resp?.total ?? list.length);
            } catch (err) {
                if (cancelled) return;
                setError(denialCopy(err, caps).message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [page, caps]);

    const handleCreate = useCallback(async ({ durationHours, maxUses }) => {
        setCreateBusy(true);
        setCreateError(null);
        try {
            const resp = await restPost("/v1/provisioning-tokens", {
                duration_hours: durationHours,
                max_uses: maxUses,
            });
            setOverlay({
                jwt: resp?.jwt ?? "",
                jwtId: resp?.jwtId ?? resp?.jwt_id ?? "",
                expiresAt: resp?.expiresAt ?? resp?.expires_at ?? null,
            });
            // Refresh the list from page 0 so the new token shows.
            setPage(0);
            await refetch(0);
        } catch (err) {
            console.error("[provisioning tokens] create", err);
            // Role-aware copy on a denial (fail-open race); the backend's own
            // message otherwise (e.g. a 4xx validation rejection of the inputs).
            setCreateError(denialOrRawMessage(err, caps));
        } finally {
            setCreateBusy(false);
        }
    }, [refetch, caps]);

    const handleRevoke = useCallback(async (jwtId) => {
        setConfirmRevoke(null);
        setRevoking((m) => ({ ...m, [jwtId]: "pending" }));
        try {
            await restDelete(`/v1/provisioning-tokens/${encodeURIComponent(jwtId)}`);
            // Mark revoked in-place; saves a full refetch.
            setRows((prev) =>
                prev.map((r) =>
                    r.jwtId === jwtId
                        ? { ...r, revoked: true, status: "revoked", revokedAt: new Date() }
                        : r,
                ),
            );
            setRevoking((m) => {
                const { [jwtId]: _, ...rest } = m;
                return rest;
            });
        } catch (err) {
            console.error("[provisioning tokens] revoke", err);
            setRevoking((m) => ({ ...m, [jwtId]: denialOrRawMessage(err, caps) }));
        }
    }, [caps]);

    const tableColumns = useMemo(
        () => getProvisioningTokenColumns(canManageTokens),
        [canManageTokens],
    );
    const cols = useDataTableColumns({
        storageKey: "provisioningTokens",
        columns: tableColumns,
        defaultVisible: tableColumns.map((c) => c.id),
    });
    const emptyState = provisioningTokenEmptyState(canManageTokens, error);

    const renderCell = useCallback(
        (r, col) => {
            switch (col.id) {
                case "status":
                    return <StatusCell status={r.status} />;
                case "createdAt":
                    return <TimestampCell value={r.createdAt} />;
                case "expiresAt":
                    return <TimestampCell value={r.expiresAt} />;
                case "usage":
                    return <UsageCell used={r.endpointsRegistered} max={r.maxUses} />;
                case "createdByEmail":
                    return (
                        <OverflowTooltip
                            as="strong"
                            text={r.createdByEmail || "—"}
                            className="text-[11px] leading-[1.08] font-medium"
                        />
                    );
                case "jwtId":
                    return (
                        <OverflowTooltip
                            as="strong"
                            text={r.jwtId}
                            className="text-[11px] leading-[1.08] font-mono text-[color:var(--muted)]"
                        />
                    );
                case "_actions": {
                    // Hide the revoke affordance entirely without manage.
                    if (!canManageTokens) return null;
                    const revokeState = revoking[r.jwtId];
                    const canRevoke = r.status === "active" && !revokeState;
                    const inConfirm = confirmRevoke === r.jwtId;
                    if (inConfirm) {
                        return (
                            <span className="flex items-center gap-[4px]">
                                <button
                                    type="button"
                                    onClick={() => handleRevoke(r.jwtId)}
                                    className="text-[10px] uppercase tracking-[0.08em] text-red-400 hover:text-red-300 cursor-pointer"
                                >
                                    Confirm
                                </button>
                                <span className="text-[color:var(--muted-soft)]">·</span>
                                <button
                                    type="button"
                                    onClick={() => setConfirmRevoke(null)}
                                    className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                                >
                                    Cancel
                                </button>
                            </span>
                        );
                    }
                    if (revokeState === "pending") {
                        return <span className={`${NOTE_CLASS} text-[10px]`}>Revoking…</span>;
                    }
                    if (revokeState) {
                        return (
                            <span className="text-[10px] text-red-400" title={revokeState}>
                                Failed
                            </span>
                        );
                    }
                    if (canRevoke) {
                        return (
                            <button
                                type="button"
                                onClick={() => setConfirmRevoke(r.jwtId)}
                                className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                            >
                                Revoke
                            </button>
                        );
                    }
                    return null;
                }
                default:
                    return null;
            }
        },
        [revoking, confirmRevoke, handleRevoke, canManageTokens],
    );

    // Column picker is always available (it's a view preference, not a
    // management action); the Create button is hidden without manage.
    const createAction = (
        <div className="flex items-center gap-[6px]">
            {canManageTokens ? (
                <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
                    <button
                        type="button"
                        onClick={() => {
                            setCreateError(null);
                            setOverlay((cur) => (cur === "form" ? null : "form"));
                        }}
                        aria-pressed={overlay === "form"}
                        className="inline-flex items-center px-[10px] text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
                    >
                        <span className={`${ACTION_TEXT_CLASS} text-[color:var(--muted)]`}>
                            Create token
                        </span>
                    </button>
                </div>
            ) : null}
            <ColumnPicker {...cols.pickerProps} />
        </div>
    );

    return (
        <>
            <AdminViewHeader
                title={viewDef?.label ?? "Provisioning Tokens"}
                scope="All endpoints"
                tabs={endpointTabs}
                actions={createAction}
            />
            <div className="flex flex-1 min-h-0 min-w-0 flex-col py-[8px]">
                <div className="shrink-0 grid gap-[10px] pb-[2px]">
                    <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                        <span>
                            <strong className="text-[color:var(--ink)]">{formatCompact(rows.length)}</strong> shown
                        </span>
                        {total != null ? (
                            <span>
                                <strong className="text-[color:var(--ink)]">{formatCompact(total)}</strong> total
                            </span>
                        ) : null}
                        {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
                        {error ? <span className="text-red-400">{error}</span> : null}
                    </div>
                </div>

                <OverlayDialog
                    open={overlay === "form"}
                    onClose={() => setOverlay(null)}
                    analyticsId={DIALOG.TOKEN_CREATE}
                    eyebrow="Provisioning"
                    title="Create token"
                    width="560px"
                >
                    <CreateForm
                        onCreate={handleCreate}
                        busy={createBusy}
                        error={createError}
                    />
                </OverlayDialog>
                <OverlayDialog
                    open={!!(overlay && typeof overlay === "object")}
                    onClose={() => setOverlay(null)}
                    analyticsId={DIALOG.TOKEN_CREATED}
                    eyebrow="Provisioning"
                    title="Token created"
                    width="720px"
                >
                    {overlay && typeof overlay === "object" ? (
                        <JwtResult
                            jwt={overlay.jwt}
                            jwtId={overlay.jwtId}
                            expiresAt={overlay.expiresAt}
                        />
                    ) : null}
                </OverlayDialog>

                <DataTable
                    cols={cols}
                    rows={rows}
                    getRowId={(r) => r.jwtId}
                    renderCell={renderCell}
                    loading={loading}
                    skeletonRows={4}
                    empty={
                        <EmptyState
                            className="my-[12px]"
                            title={emptyState.title}
                            note={emptyState.note}
                        />
                    }
                    footer={
                        total != null && rows.length < total ? (
                            <div className="grid place-items-center pt-[8px] pb-[10px]">
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => p + 1)}
                                    disabled={loading}
                                    className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 cursor-pointer"
                                >
                                    Load more · {formatCompact(total - rows.length)} remaining
                                </button>
                            </div>
                        ) : null
                    }
                />
            </div>
        </>
    );
}
